import { useEffect, useState, Component, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, AlertTriangle, Edit3, X, Check } from "lucide-react";
import { api } from "../api";
import type { Member, CheckIn } from "../types";
import { ContactLog } from "./ContactLog";
import { WeightTrendChart } from "./WeightTrendChart";

// Prevent any section from crashing the whole page
class SectionBoundary extends Component<{ children: ReactNode; label: string }, { hasError: boolean }> {
  declare props: { children: ReactNode; label: string };
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-500">Something went wrong loading the {this.props.label} section.</div>;
    return this.props.children;
  }
}

const STATUS_OPTIONS = ["Active", "Paused", "Payment Issues", "Expired/Former"];
const PAYMENT_PLANS = ["Free", "Week-to-week subscription", "6 month membership", "12 month membership", "12 week membership"];
const MEMBERSHIP_TYPES = ["Basic", "VIP"];
const COACHES = ["Henny", "Lily"];

const STATUS_COLORS: Record<string, string> = {
  Active: "bg-emerald-100 text-emerald-700",
  Paused: "bg-amber-100 text-amber-700",
  "Payment Issues": "bg-red-100 text-red-700",
  "Expired/Former": "bg-gray-100 text-gray-500",
};

/** Normalize any date string to YYYY-MM-DD */
function toDateString(v: string | null | undefined): string {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
}

function fmtDate(d: string | null): string {
  const ds = toDateString(d);
  if (!ds) return "\u2014";
  const [y, m, dd] = ds.split("-");
  return `${dd}/${m}/${y}`;
}

export function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addWeeksVal, setAddWeeksVal] = useState(1);
  const [showCheckinDetail, setShowCheckinDetail] = useState<CheckIn | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadMember = () => {
    if (!id) return;
    setLoading(true);
    Promise.allSettled([api.getMember(id), api.getMemberCheckIns(id)])
      .then(([memberRes, checkinsRes]) => {
        if (memberRes.status === "fulfilled") {
          setMember(memberRes.value.member);
          setEditForm(memberRes.value.member);
        }
        if (checkinsRes.status === "fulfilled") setCheckins(checkinsRes.value.checkins);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMember(); }, [id]);

  const handleSave = async () => {
    if (!member) return;
    setSaving(true);
    try {
      const res = await api.updateMember(member.id, editForm);
      setMember(res.member);
      setEditForm(res.member);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleAddWeeks = async () => {
    if (!member || addWeeksVal < 1) return;
    setSaving(true);
    try {
      const res = await api.addWeeks(member.id, addWeeksVal);
      setMember(res.member);
      setEditForm(res.member);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!member) return;
    if (!confirm(`Are you sure you want to delete ${member.name}? This cannot be undone.`)) return;
    try {
      await api.deleteMember(member.id);
      navigate("/members");
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteCheckin = async (checkinId: string) => {
    if (!confirm("Delete this check-in record? This cannot be undone.")) return;
    setDeletingId(checkinId);
    try {
      await api.deleteCheckIn(checkinId);
      setCheckins((prev) => prev.filter((c) => c.id !== checkinId));
      setShowCheckinDetail(null);
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateCheckin = async (checkinId: string, updates: Record<string, any>) => {
    try {
      const res = await api.updateCheckIn(checkinId, updates);
      setCheckins((prev) => prev.map((c) => c.id === checkinId ? { ...c, ...res.checkin } : c));
      // Also update the modal if open
      setShowCheckinDetail((prev) => prev && prev.id === checkinId ? { ...prev, ...res.checkin } : prev);
    } catch (e) {
      console.error(e);
    }
  };

  const updateField = (field: string, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="space-y-4 animate-pulse"><div className="h-8 bg-gray-100 rounded w-48" /><div className="h-40 bg-gray-100 rounded-xl" /><div className="h-64 bg-gray-100 rounded-xl" /></div>;
  if (!member) return <div className="text-center py-20"><p className="text-gray-400 text-lg">Member not found</p><button onClick={() => navigate("/members")} className="text-[#5f1b2c] hover:text-[#4a1520] text-sm mt-2">Back to members</button></div>;

  const isExpired = member.end_date && toDateString(member.end_date) < new Date().toISOString().split("T")[0];
  const showWarning = isExpired && (member.status === "Paused" || member.status === "Payment Issues");

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/members")} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Members</button>

      {/* Warning badge */}
      {showWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0" />
          <span className="text-sm text-orange-700 font-medium">Membership end date has passed but status is "{member.status}". Please review and update as needed.</span>
        </div>
      )}

      {/* Member profile card */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-5 mb-6">
          <div className="w-16 h-16 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-xl shrink-0">
            {member.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{member.name}</h1>
          </div>
          <button onClick={handleDelete} className="text-gray-400 hover:text-red-500 transition-colors p-2"><Trash2 className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" value={editForm.name} onChange={(v) => updateField("name", v)} />
          <Field label="Email" value={editForm.email || ""} onChange={(v) => updateField("email", v)} type="email" />
          <Field label="Phone" value={editForm.phone || ""} onChange={(v) => updateField("phone", v)} />
          <SelectField label="Membership Type" value={editForm.membership_type || "Basic"} options={MEMBERSHIP_TYPES} onChange={(v) => updateField("membership_type", v)} />
          <SelectField label="Payment Plan" value={editForm.payment_plan || ""} options={PAYMENT_PLANS} onChange={(v) => updateField("payment_plan", v)} placeholder="None" />
          <SelectField label="Status" value={editForm.status || "Active"} options={STATUS_OPTIONS} onChange={(v) => updateField("status", v)} />
          {editForm.status === "Payment Issues" && (
            <Field label="Follow Up On" value={editForm.follow_up_date || ""} onChange={(v) => updateField("follow_up_date", v)} type="date" />
          )}
          <SelectField label="Coach Assigned" value={editForm.coach_assigned || ""} options={COACHES} onChange={(v) => updateField("coach_assigned", v)} placeholder="None" />
          <Field label="Start Date" value={editForm.start_date || ""} onChange={(v) => updateField("start_date", v)} type="date" />
          <Field label="End Date" value={editForm.end_date || ""} onChange={(v) => updateField("end_date", v)} type="date" />
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-gray-500 mb-1 block">Goals</label>
          <textarea value={editForm.goals || ""} onChange={(e) => updateField("goals", e.target.value)} rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] resize-none" />
        </div>
        <div className="mt-4">
          <label className="text-xs font-medium text-gray-500 mb-1 block">Notes</label>
          <textarea value={editForm.notes || ""} onChange={(e) => updateField("notes", e.target.value)} rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] resize-none" />
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] transition-colors disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}</button>
        </div>
      </div>

      {/* Add Weeks */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Extend Membership</h3>
        <div className="flex items-center gap-3">
          <input type="number" min={1} max={52} value={addWeeksVal} onChange={(e) => setAddWeeksVal(parseInt(e.target.value) || 1)} className="w-20 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]" />
          <span className="text-sm text-gray-600">weeks</span>
          <button onClick={handleAddWeeks} disabled={saving} className="px-4 py-2 bg-[#e2c4b6] text-[#5f1b2c] rounded-lg text-sm font-medium hover:bg-[#d4b3a3] transition-colors disabled:opacity-50">Add Weeks</button>
          {member.end_date && <span className="text-xs text-gray-400 ml-2">Current end: {fmtDate(member.end_date)}</span>}
        </div>
      </div>

      {/* Weight Trend */}
      <SectionBoundary label="weight trend">
        {checkins.length > 0 && <WeightTrendChart checkins={checkins} />}
      </SectionBoundary>

      {/* Contact Log — above check-in history */}
      {member && <ContactLog memberId={member.id} />}

      {/* Check-in history — now editable */}
      <SectionBoundary label="check-in history">
        <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Check-In History ({checkins.length})</h2>
        {checkins.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">No check-ins recorded yet</div>
        ) : (
          <div className="space-y-2">
            {checkins.map((c) => (
              <button key={c.id} onClick={() => setShowCheckinDetail(c)} className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-[#e2c4b6] hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">{c.week_label || fmtDate(c.week_start_date)}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${c.member_submitted ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{c.member_submitted ? "Submitted" : "Not submitted"}</span>
                    {c.coach_reviewed && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">Reviewed</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    {c.current_weight && <span>{c.current_weight} kg</span>}
                    {c.checkin_method_this_week && <span className="text-xs text-gray-400">{c.checkin_method_this_week}</span>}
                    <Edit3 className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      </SectionBoundary>

      {/* Check-in detail modal — now editable with delete */}
      {showCheckinDetail && (
        <CheckinEditModal
          checkin={showCheckinDetail}
          onClose={() => setShowCheckinDetail(null)}
          onSave={handleUpdateCheckin}
          onDelete={handleDeleteCheckin}
          deleting={deletingId === showCheckinDetail.id}
        />
      )}
    </div>
  );
}

/** Editable check-in modal with save and delete */
function CheckinEditModal({ checkin, onClose, onSave, onDelete, deleting }: {
  checkin: CheckIn;
  onClose: () => void;
  onSave: (id: string, updates: Record<string, any>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  deleting: boolean;
}) {
  const [method, setMethod] = useState(checkin.checkin_method_this_week || "Voice message");
  const [weight, setWeight] = useState(checkin.current_weight != null ? String(checkin.current_weight) : "");
  const [drive, setDrive] = useState(checkin.google_drive_link || "");
  const [notes, setNotes] = useState(checkin.client_checkin_notes || "");
  const [photo, setPhoto] = useState(checkin.photo_submitted);
  const [submitted, setSubmitted] = useState(checkin.member_submitted);
  const [reviewed, setReviewed] = useState(checkin.coach_reviewed);
  const [localDate, setLocalDate] = useState(() => {
    if (!checkin.check_in_date) return "";
    return toDateString(checkin.check_in_date);
  });
  const [dirty, setDirty] = useState<Record<string, any>>({});

  const markDirty = (field: string, value: any) => {
    setDirty((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const payload = { ...dirty };
    if (Object.keys(payload).length > 0) {
      await onSave(checkin.id, payload);
    }
    setDirty({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{checkin.week_label || "Check-In"}</h3>
            <p className="text-sm text-gray-500">{fmtDate(checkin.week_start_date)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          {/* Check-in Date */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Check-In Date</label>
            <input
              type="date"
              value={localDate}
              onChange={(e) => { setLocalDate(e.target.value); markDirty("check_in_date", e.target.value || null); }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
            />
          </div>

          {/* Method */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Check-in Method</label>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white w-fit">
              {["Voice message", "DM"].map((m) => (
                <button
                  key={m}
                  onClick={() => { setMethod(m); markDirty("checkin_method_this_week", m); }}
                  className={`px-4 py-2 text-xs font-medium transition-colors ${method === m ? "bg-[#5f1b2c] text-white" : "text-gray-600 hover:bg-gray-50"}`}
                >{m}</button>
              ))}
            </div>
          </div>

          {/* Weight */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Weight</label>
            <input
              type="text"
              inputMode="decimal"
              value={weight}
              onChange={(e) => { setWeight(e.target.value); markDirty("current_weight", e.target.value ? parseFloat(e.target.value) : null); }}
              placeholder="kg"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
            />
          </div>

          {/* Google Drive Link */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Google Drive Link</label>
            <input
              type="text"
              value={drive}
              onChange={(e) => { setDrive(e.target.value); markDirty("google_drive_link", e.target.value || null); }}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Check-In Notes</label>
            <textarea
              value={notes}
              onChange={(e) => { setNotes(e.target.value); markDirty("client_checkin_notes", e.target.value || null); }}
              rows={4}
              placeholder="Notes from this week's check-in..."
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c] resize-y min-h-[100px]"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex items-center gap-6 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={photo} onChange={(e) => { setPhoto(e.target.checked); markDirty("photo_submitted", e.target.checked); }} className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]" />
              <span className="text-sm text-gray-700">Photo submitted</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={submitted} onChange={(e) => { setSubmitted(e.target.checked); markDirty("member_submitted", e.target.checked); }} className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]" />
              <span className="text-sm text-gray-700">Member submitted</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={reviewed} onChange={(e) => { setReviewed(e.target.checked); markDirty("coach_reviewed", e.target.checked); }} className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]" />
              <span className="text-sm text-gray-700">Coach reviewed</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={() => onDelete(checkin.id)}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? "Deleting..." : "Delete"}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">Cancel</button>
            <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] transition-colors">
              <Check className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  const isDate = type === "date";
  const displayValue = isDate ? toDateString(value) : value;
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
      <input type={type} value={displayValue} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] focus:ring-1 focus:ring-[#5f1b2c]/30" />
    </div>
  );
}

function SelectField({ label, value, options, onChange, placeholder }: { label: string; value: string; options: string[]; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}